import React, { Fragment, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { FETCH_TRIPS, UPDATE_TRIP_CLASSIFICATION } from 'actions';
import { useAuth } from 'components/Auth';
import { useSelector, useDispatch } from 'react-redux';
import _ from 'lodash';
import { format, startOfDay, endOfDay, addDays, subDays } from 'date-fns';
import { DatePicker } from '@material-ui/pickers';
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Chip,
  IconButton,
} from '@material-ui/core';
import { ClassificationDialog } from '../dialogs';
import {
  Label as LabelIcon,
  ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon,
} from '@material-ui/icons';
import { useLongPress } from '../../hooks';
import { FETCH_REPLAY } from '../../actions';
import ReplayMap from './IndividualReplayMap';

const useStyles = makeStyles((theme) => ({
  calendar: {
    justifyContent: 'center',
    display: 'flex',
    paddingTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  datepicker: {
    width: 100,
  },
  chevronFwd: {
    paddingTop: theme.spacing(0.5),
    width: theme.spacing(8),
    textAlign: 'center',
    '& buttonDisabled': {
      color: theme.palette.grey[900],
    },
  },
  chevronBack: {
    paddingTop: theme.spacing(0.5),
    width: theme.spacing(8),
    textAlign: 'center',
  },
  list: {
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    paddingRight: theme.spacing(2),
    overflowY: 'auto',
    display: 'inlineFlex',
    height: 800,
  },
  classifcationMenu: {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(13),
    alignItems: 'center',
    position: 'fixed',
    paddingBottom: theme.spacing(0.5),
    paddingTop: theme.spacing(0.5),
    bottom: '0%',
    width: '100%',
    backgroundColor: 'white',
    opacity: 1,
  },
  container: {
    height: '100%',
  },
  map: {
    width: '100%',
    height: '50%',
  },
  selectedItem: {
    color: theme.palette.primary.main,
  },
}));

function LongPressableListItem(props) {
  const { onLongPress, ...otherProps } = props;
  const longPress = useLongPress(onLongPress, 500);

  return <ListItem {...otherProps} {...longPress} />;
}

export default function IndividualTrips() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const auth = useAuth();
  const { code } = auth.getProfile();

  const trips = useSelector((state) => state.events.trips.list, _.isEqual);
  const paths = useSelector((state) => state.replay.paths, _.isEqual);

  const [selectedListItem, setSelectedListItem] = useState(null);
  const [selectedDate, handleDateChange] = useState(new Date());
  const [
    tripIdToChangeClassification,
    setTripIdToChangeClassification,
  ] = useState(null);
  const [checkedIdentifiers, setCheckedIdentifiers] = useState([]);
  const [classificationOpen, setClassificationOpen] = useState(false);

  const isSelectedDateToday =
    startOfDay(selectedDate).getTime() === startOfDay(new Date()).getTime();

  const onItemChecked = (trip) => (event) => {
    event.stopPropagation();
    setCheckedIdentifiers((oldValue) => {
      if (event.target.checked) {
        return [...oldValue, trip.identifier];
      } else {
        return oldValue.filter((id) => id !== trip.identifier);
      }
    });
  };

  const handleClassificationClick = (trip) => () => {
    setTripIdToChangeClassification(trip);
  };

  const onAllItemsCheckboxClick = (event) => {
    const checked = event.target.checked;

    if (checked) {
      setCheckedIdentifiers(trips.map((trip) => trip.identifier));
    } else {
      setCheckedIdentifiers([]);
    }
  };

  function formatDate(date) {
    return format(date, 'hh:mm');
  }

  function formatDistance(distance) {
    return (Math.round(distance * 100) / 100).toFixed(2);
  }

  const handleTripClick = (trip) => () => {
    setSelectedListItem(trip);
    dispatch({
      type: FETCH_REPLAY,
      payload: { type: 'trips', id: trip.identifier },
    });
  };

  const tripList = trips.map((trip) => (
    <LongPressableListItem
      button
      disableRipple={tripIdToChangeClassification}
      onLongPress={handleClassificationClick(trip)}
      onClick={handleTripClick(trip)}
      key={trip.identifier}
    >
      <ListItemIcon>
        <Checkbox
          edge="start"
          checked={checkedIdentifiers.includes(trip.identifier)}
          onClick={onItemChecked(trip)}
          tabIndex={-1}
          disableRipple
          inputProps={{ 'aria-labelledby': trip.identifier }}
        />
      </ListItemIcon>
      <ListItemText
        className={selectedListItem === trip ? classes.selectedItem : undefined}
        primaryTypographyProps={{
          variant: 'body2',
          style: { lineHeight: 1.3, marginTop: 4 },
        }}
        primary={
          formatDate(new Date(trip.startTime)) +
          ' - ' +
          formatDate(new Date(trip.endTime)) +
          ' ' +
          formatDistance(trip.distanceMiles) +
          'M'
        }
        secondaryTypographyProps={{ component: 'span', noWrap: true }}
        secondary={trip.vehicle.registrationNumber}
      />
      {trip.classification !== 'None' && (
        <Chip
          label={trip.classification}
          onClick={handleClassificationClick(trip)}
        />
      )}
    </LongPressableListItem>
  ));

  function handleClassificationClose(classification) {
    if (classification) {
      dispatch({
        type: UPDATE_TRIP_CLASSIFICATION,
        payload: {
          id: tripIdToChangeClassification.identifier,
          classification: classification,
        },
      });
      updateQuery(selectedDate);
    }
    setTripIdToChangeClassification(null);
  }

  function updateQuery(newValue) {
    setCheckedIdentifiers([]);
    dispatch({
      type: FETCH_TRIPS,
      payload: {
        'driver.code': { $in: [code] },
        endTime: {
          $gte: startOfDay(new Date(newValue)).toISOString(),
        },
        startTime: { $lt: endOfDay(new Date(newValue)).toISOString() },
      },
    });
  }

  function handleLabelClassificationClick() {
    setClassificationOpen(true);
  }

  function handleLabelClassificationClose(classification) {
    if (classification) {
      checkedIdentifiers.forEach((id) =>
        dispatch({
          type: UPDATE_TRIP_CLASSIFICATION,
          payload: { id: id, classification },
        })
      );
    }
    setClassificationOpen(false);
  }

  return (
    <Fragment>
      <ReplayMap
        isMobilePreviewMap
        paths={paths}
        followedItemIndexes={[]}
        selectedItemIndex={{}}
        hoveredItemIndex={{}}
      />
      <div className={classes.container}>
        <div className={classes.calendar}>
          <IconButton
            className={classes.chevronBack}
            onClick={() => {
              handleDateChange(subDays(selectedDate, 1));
              updateQuery(subDays(selectedDate, 1));
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
          <DatePicker
            className={classes.datepicker}
            maxDate={new Date()}
            openTo="date"
            value={selectedDate}
            onChange={(newValue) => {
              const newDate = newValue.toDate();
              handleDateChange(newDate);
              updateQuery(newDate);
            }}
            inputProps={{
              style: { textAlign: 'center' },
            }}
            InputProps={{ disableUnderline: true }}
          />
          <IconButton
            disabled={isSelectedDateToday}
            classes={{
              root: classes.chevronFwd,
              disabled: classes.chevronDisabled,
            }}
            onClick={() => {
              handleDateChange(addDays(selectedDate, 1));
              updateQuery(addDays(selectedDate, 1));
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </div>
        <List className={classes.list}>{tripList}</List>
        {checkedIdentifiers.length > 0 && (
          <div className={classes.classifcationMenu}>
            <Checkbox
              checked={checkedIdentifiers.length === trips.length}
              onClick={onAllItemsCheckboxClick}
            />
            <Typography>{checkedIdentifiers.length} selected</Typography>
            <IconButton
              title="Classification"
              color="primary"
              onClick={handleLabelClassificationClick}
            >
              <LabelIcon />
            </IconButton>
          </div>
        )}
        <ClassificationDialog
          open={!!tripIdToChangeClassification}
          onClose={handleClassificationClose}
        />
        <ClassificationDialog
          open={classificationOpen}
          onClose={handleLabelClassificationClose}
        />
      </div>
    </Fragment>
  );
}
